/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {FilterBarEl} from 'common/components/filter-bar';
import {FilterBarTableEl} from 'common/components/filter-bar-table';
import {TableEl} from 'common/components/table';
import {FieldWithMatches} from 'common/components/table/cell-mixins';

import {CodeTipEl} from '../components/code-tip';
import type {DesignSystem} from '../index';
import tableEmojiListStyleOverride from '../fixtures/table-emoji-list.inline.styl';
import {Section, SubSection} from './utils';

function cellCategory(item, context) {
  // example of custom search match parts handling
  return (
    <div sel='.tag-category'>
      <FieldWithMatches item={item} context={context} />
    </div>
  );
}

function cellEmoji(item) {
  return <div sel='.cell-emoji'>{item.emoji}</div>;
}

export function TableSection({designSystem}: {designSystem: DesignSystem}) {
  return (
    <Section title='Table'>
      <SubSection title='mp-filter-bar' autoDoc={true}>
        <p sel='.desc'>
          Used in conjunction with mp-table to provide a way to filter results and see number of results.
        </p>

        <CodeTipEl sel='.example.block'>
          <FilterBarEl
            attrs={{
              'result-count': `22 results`,
              filters: [
                {
                  name: `language`,
                  multiple: false,
                  sections: [
                    {
                      items: [{label: `All Platforms`}, {label: `python`}, {label: `node`}, {label: `go`}],
                    },
                  ],
                },
                {
                  name: `platform`,
                  multiple: true,
                  sections: [
                    {
                      items: [
                        {label: `All Systems`},
                        {label: `windows`},
                        {label: `osx`, isSelected: true},
                        {label: `linux`, isSelected: true},
                      ],
                    },
                  ],
                },
              ],
              actions: [
                {
                  disabled: false,
                  disabledTooltip: `You do not have permission to mark as expected`,
                  label: `Mark as Expected`,
                  icon: `circle-checkmark`,
                  eventName: `violation`,
                },
              ],
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-table' autoDoc={true}>
        <p sel='.desc'>
          For displaying tabular data with columns and rows. Implementor component can provide custom cell rendering
          mixins. #[br] mp-table can load, scroll, sort and search 1000s of rows.
        </p>

        <CodeTipEl sel='.example'>
          <TableEl
            attrs={{
              'track-name': `design-system-example`,
              'multi-select': true,
              'max-body-height': `300px`,
              'style-override': tableEmojiListStyleOverride,
            }}
            on={{
              selectionChange: designSystem.helpers.handleTableSelectionChange,
            }}
            props={{
              rows: designSystem.state.tableRows,
              columns: [
                {
                  field: `emoji`,
                  title: `Emoji`,
                  cellRenderer: cellEmoji,
                  flexBasis: `100px`,
                  helpText: `Special text icon used to express an idea or emotion`,
                  isGroupable: true,
                },
                {
                  field: `name`,
                  title: `Name`,
                  type: `text-link`,
                  isGroupable: true,
                  isSortable: true,
                  isSearchable: true,
                  flexGrow: `2`,
                },
                {
                  field: `category`,
                  title: `Category`,
                  cellRenderer: cellCategory,
                  isSortable: true,
                  isSearchable: true,
                  flexGrow: `1`,
                },
              ],
            }}
          />
        </CodeTipEl>
        <br />
        <CodeTipEl sel='.example'>
          <TableEl
            attrs={{
              'track-name': `design-system-example`,
              'max-body-height': `300px`,
              'style-override': tableEmojiListStyleOverride,
            }}
            on={{
              rowDetailsVisibilityChange: designSystem.helpers.handleRowDetailsVisibilityChange,
            }}
            props={{
              rows: designSystem.state.tableRows,
              rowDetailsRenderer: (row) => <div sel='.item-details'>More details about {row[`name`]}</div>,
              columns: [
                {
                  field: `emoji`,
                  title: `Emoji`,
                  cellRenderer: cellEmoji,
                  flexBasis: `100px`,
                  helpText: `Special text icon used to express an idea or emotion`,
                },
                {
                  field: `name`,
                  title: `Name`,
                  type: `text-link`,
                  isSortable: true,
                  isSearchable: true,
                  flexGrow: `2`,
                },
                {
                  field: `category`,
                  title: `Category`,
                  cellRenderer: cellCategory,
                  isSortable: true,
                  isSearchable: true,
                  flexGrow: `1`,
                },
              ],
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-filter-bar-table' autoDoc={true}>
        <p sel='.desc'>
          This is a convenience component stitches together `mp-filter-bar` and `mp-table` since they are often used
          together. It has pre-written convenience methods for filtering `rows` based on `filterConfigs` and `search`
          values, though you must still write certain things yourself, such as `onTabChange`.
        </p>

        <CodeTipEl sel='.example' style={{display: `block`}}>
          <FilterBarTableEl
            props={{
              tabs: [
                {value: `mine`, label: `My Cars`},
                {value: `all`, label: `All Cars`},
              ],
              selectedTab: designSystem.state.filterBarTableSelectedTab,
              rows: designSystem.state.filterBarTableRows,
              columns: [
                {
                  field: `licensePlate`,
                  title: `License Plate`,
                  isSearchable: true,
                  helpText: `Helpful text`,
                  flexGrow: `1`,
                },
                {
                  field: `make`,
                  title: `Make`,
                  isSortable: true,
                  isSearchable: true,
                  flexGrow: `1`,
                },
                {
                  field: `model`,
                  title: `Model`,
                  isSortable: true,
                  isSearchable: true,
                  flexGrow: `1`,
                },
                {
                  field: `year`,
                  title: `Year`,
                  isSearchable: true,
                  flexGrow: `1`,
                },
              ],

              filterSelectConfigs: designSystem.helpers.getFilterBarTableSelectConfigs(),

              filterSearchPlaceholder: `Search cars`,

              tableAttrs: {
                'default-sort-field': `count`,
              },
            }}
            on={{
              tabChange: designSystem.helpers.handleFilterBarTableTabChange,
            }}
          />
        </CodeTipEl>
      </SubSection>
    </Section>
  );
}
